import {
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useCallback, useRef, useState } from 'react';

import type {
  PipelineCard,
  PipelineColumn,
  PipelineDragMove,
} from '@/pages/pipelines/common/types';

import { settleDroppedCard } from '../utils';

type Params = {
  columns: PipelineColumn[];
  expandedStage: string;
  setExpandedStage: (_stage: string) => void;
  onMoveCard?: (_move: PipelineDragMove) => Promise<void> | void;
  addOptimisticMove: (_move: PipelineDragMove) => () => void;
};

// Owns the dnd-kit drag lifecycle for pipeline cards: which card is active (for
// the drag overlay), the sensors, and translating a drop into an optimistic move
// + settle animation. Kept out of the board component so it stays render-focused.
export const usePipelineCardDrag = ({
  columns,
  expandedStage,
  setExpandedStage,
  onMoveCard,
  addOptimisticMove,
}: Params) => {
  const [activeCard, setActiveCard] = useState<PipelineCard | null>(null);
  const expandedStageBeforeDragRef = useRef(expandedStage);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const findCard = useCallback(
    (cardId: string) => {
      for (const column of columns) {
        const card = column.cards.find((candidate) => candidate.id === cardId);
        if (card) return card;
      }
      return null;
    },
    [columns],
  );

  const onDragStart = (event: DragStartEvent) => {
    const card = findCard(String(event.active.id));
    if (!card) return;
    expandedStageBeforeDragRef.current = expandedStage;
    setActiveCard(card);
  };

  const onDragCancel = () => {
    setActiveCard(null);
    setExpandedStage(expandedStageBeforeDragRef.current);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const card = activeCard;
    // Where the card was released, taken from dnd-kit's own geometry (the dragged
    // item's translated rect) so the drop can animate from there into its slot.
    const releasedFrom = event.active.rect.current.translated;
    setActiveCard(null);

    const sourceStage = card?.stage;
    const targetStage = event.over ? String(event.over.id) : null;
    if (!card || !sourceStage || !targetStage || targetStage === sourceStage) {
      setExpandedStage(expandedStageBeforeDragRef.current);
      return;
    }

    const move: PipelineDragMove = {
      cardId: card.id,
      sourceStage,
      sourceIndex: 0,
      targetStage,
      targetIndex: 0,
    };

    // Apply the move optimistically and expand the target so the card is already
    // rendered in its final slot, then FLIP-animate it from the release point into
    // that slot on the next frame. Revert the optimistic move if the request fails.
    const revertOptimisticMove = addOptimisticMove(move);
    setExpandedStage(targetStage);
    void Promise.resolve(onMoveCard?.(move))
      .then(() => setExpandedStage(move.targetStage))
      .catch(() => {
        revertOptimisticMove();
        setExpandedStage(move.sourceStage);
      });

    if (releasedFrom) {
      requestAnimationFrame(() => settleDroppedCard(move.cardId, releasedFrom));
    }
  };

  return {
    activeCard,
    sensors,
    dragSourceStage: activeCard?.stage ?? null,
    onDragStart,
    onDragCancel,
    onDragEnd,
  };
};
